import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Minus, Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useT } from "@/i18n";

const ITEM_H = 40;
const WHEEL_H = ITEM_H * 5;

function WheelPortal({ value, min, max, pos, onPick, onClose, testid }) {
  const ref = useRef(null);
  const timer = useRef(null);
  const vals = [];
  for (let i = min; i <= max; i++) vals.push(i);
  const clamp = (n) => Math.min(max, Math.max(min, n));

  const scrollTo = (v) => { if (ref.current) ref.current.scrollTop = Math.max(0, v - min) * ITEM_H; };

  useEffect(() => {
    scrollTo(value ?? min);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line

  const onScroll = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (!ref.current) return;
      const idx = Math.round(ref.current.scrollTop / ITEM_H);
      onPick(clamp(min + idx));
    }, 90);
  };

  const jump = (delta) => { const nv = clamp((value ?? min) + delta); onPick(nv); scrollTo(nv); };

  const below = pos.spaceBelow >= WHEEL_H + 48;
  const style = { left: pos.left, width: 108, [below ? "top" : "bottom"]: below ? pos.bottom + 4 : pos.fromBottom + 4 };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[100]" onClick={onClose} data-testid={`${testid}-overlay`} />
      <div className="fixed z-[101] -translate-x-1/2 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden" style={style} data-testid={`${testid}-wheel`}>
        <div className="flex items-center justify-between px-2 py-1 border-b border-slate-100">
          <button type="button" onClick={() => jump(-10)} data-testid={`${testid}-jump-down`} className="text-[10px] font-bold text-slate-500 hover:text-brand px-1">−10</button>
          <button type="button" onClick={() => jump(10)} data-testid={`${testid}-jump-up`} className="text-[10px] font-bold text-slate-500 hover:text-brand px-1">+10</button>
        </div>
        <div className="relative" style={{ height: WHEEL_H }}>
          <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 border-y-2 border-brand/40 bg-brand/5" style={{ height: ITEM_H }} />
          <div ref={ref} onScroll={onScroll} className="h-full overflow-y-auto snap-y snap-mandatory no-scrollbar">
            <div style={{ height: ITEM_H * 2 }} />
            {vals.map((v) => (
              <button key={v} type="button" onClick={() => { onPick(v); onClose(); }} data-testid={`${testid}-opt-${v}`}
                className={`block w-full snap-center text-center font-mono transition-colors ${v === value ? "text-brand font-bold text-xl" : "text-slate-400 text-lg"}`}
                style={{ height: ITEM_H, lineHeight: `${ITEM_H}px` }}>
                {v}
              </button>
            ))}
            <div style={{ height: ITEM_H * 2 }} />
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

export function NumberField({ label, value, onChange, testid, min = 0, max = 100 }) {
  const t = useT();
  const btnRef = useRef(null);
  const holdRef = useRef({ to: null, iv: null });
  const cur = useRef(value ?? 0);
  cur.current = value ?? 0;
  const [pos, setPos] = useState(null);
  const clamp = (n) => Math.min(max, Math.max(min, n));

  const step = (dir) => onChange(clamp(cur.current + dir));
  const holdStart = (dir) => {
    step(dir);
    holdRef.current.to = setTimeout(() => {
      holdRef.current.iv = setInterval(() => step(dir), 80);
    }, 400);
  };
  const holdStop = () => {
    clearTimeout(holdRef.current.to);
    clearInterval(holdRef.current.iv);
    holdRef.current = { to: null, iv: null };
  };
  useEffect(() => () => holdStop(), []);

  const openWheel = () => {
    if (pos) { setPos(null); return; }
    const r = btnRef.current.getBoundingClientRect();
    setPos({ left: r.left + r.width / 2, bottom: r.bottom, fromBottom: window.innerHeight - r.top, spaceBelow: window.innerHeight - r.bottom });
  };

  const v = value ?? 0;
  const stepBtn = "w-8 h-9 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 flex items-center justify-center shrink-0 active:scale-95 active:bg-brand active:text-white transition-transform select-none";

  return (
    <div data-testid={testid}>
      {label && <Label className="text-xs">{t(label)}</Label>}
      <div className="mt-1 flex items-center gap-1">
        <button type="button" onMouseDown={() => holdStart(-1)} onMouseUp={holdStop} onMouseLeave={holdStop}
          onTouchStart={(e) => { e.preventDefault(); holdStart(-1); }} onTouchEnd={holdStop}
          data-testid={`${testid}-dec`} className={stepBtn}><Minus size={14} /></button>
        <button ref={btnRef} type="button" onClick={openWheel} data-testid={`${testid}-value`}
          className="flex-1 h-9 rounded-md border border-slate-200 bg-white font-mono font-bold text-center hover:border-brand transition-colors">{v}</button>
        <button type="button" onMouseDown={() => holdStart(1)} onMouseUp={holdStop} onMouseLeave={holdStop}
          onTouchStart={(e) => { e.preventDefault(); holdStart(1); }} onTouchEnd={holdStop}
          data-testid={`${testid}-inc`} className={stepBtn}><Plus size={14} /></button>
      </div>
      {pos && <WheelPortal value={v} min={min} max={max} pos={pos} onPick={onChange} onClose={() => setPos(null)} testid={testid} />}
    </div>
  );
}

export function OversField({ value, onChange, testid, computedBalls }) {
  const t = useT();
  const s = String(value ?? "0.0");
  const whole = s.includes(".") ? (parseInt(s.split(".")[0], 10) || 0) : (parseInt(s, 10) || 0);
  const ball = s.includes(".") ? (parseInt(s.split(".")[1][0], 10) || 0) : 0;
  return (
    <div className="col-span-2" data-testid={testid}>
      <Label className="text-xs">{t("Overs")}</Label>
      <div className="mt-1 flex items-end gap-2">
        <div className="flex-1"><NumberField label="" value={whole} min={0} max={90} onChange={(w) => onChange(`${w}.${ball}`)} testid="input-bowling-overs" /></div>
        <span className="font-head font-black text-2xl pb-1">.</span>
        <div className="w-24"><NumberField label="" value={ball} min={0} max={5} onChange={(b) => onChange(`${whole}.${b}`)} testid="input-bowling-balls" /></div>
        <span className="text-[10px] text-slate-500 pb-2 whitespace-nowrap">= {computedBalls} {t("balls")}</span>
      </div>
    </div>
  );
}
